/**
 * Effective map-setup values for a workspace.
 *
 * Existing workspaces are flagged as configured even when their persisted
 * `mapSettings` object is empty (or pre-dates individual fields). Reading the
 * raw settings then yields blanks, so read-only displays (e.g. the settings
 * summary) fall back to the same defaults the configurator suggests on first
 * run: Poland, its capital as the center, and the default zoom levels.
 *
 * @module utils/mapSettingsDefaults
 */

import { COUNTRIES } from '@/data/countries';
import {
  MAP_SETUP_DEFAULT_MAX_ZOOM,
  MAP_SETUP_DEFAULT_MIN_ZOOM,
  MAP_SETUP_DEFAULT_PL_COUNTRY_CODE,
  MAP_SETUP_DEFAULT_START_ZOOM,
} from '@/constants';
import type { Coordinates, MapSettings } from '@/types';

/** Capital-city coordinates for an ISO 3166-1 code, or null when unknown. */
export function capitalForCountry(
  countryCode: string | null
): Coordinates | null {
  if (!countryCode) return null;
  const entry = COUNTRIES.find((country) => country.code === countryCode);
  return entry ? { lat: entry.capital.lat, lng: entry.capital.lng } : null;
}

/** Read-only map-setup values with every blank filled by a default. */
export interface ResolvedMapSettings {
  countryCode: string;
  center: Coordinates | null;
  startZoom: number;
  minZoom: number;
  maxZoom: number;
}

/**
 * Resolves the effective map-setup values for display, filling defaults for an
 * empty or partial configuration (country → Poland, center → its capital, zoom
 * levels → the configurator defaults).
 */
export function resolveMapSettings(
  settings: MapSettings | null
): ResolvedMapSettings {
  const countryCode =
    settings?.countryCode || MAP_SETUP_DEFAULT_PL_COUNTRY_CODE;
  return {
    countryCode,
    center: settings?.center ?? capitalForCountry(countryCode),
    startZoom: settings?.startZoom ?? MAP_SETUP_DEFAULT_START_ZOOM,
    minZoom: settings?.minZoom ?? MAP_SETUP_DEFAULT_MIN_ZOOM,
    maxZoom: settings?.maxZoom ?? MAP_SETUP_DEFAULT_MAX_ZOOM,
  };
}
